import SharedDialog from '../../../shared/shared-dialog';
import { useState, useCallback } from 'react';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import SharedFilePicker from '../../../shared/shared-file-picker';
import SharedFilePickerOld from '../../../shared/shared-file-picker-old';
import { downloadGmailExportToDrive } from '../../../api/users-api';
import { useMutation } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { GmailAuditModel } from '../../../../types';
import { useStore } from '../../state/zustand-store';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Download from '@mui/icons-material/Download';

interface DownloadGmailExportsDialogProps {
  open: boolean;
  handleClose: () => void;
  gmailAuditModel: GmailAuditModel;
}

const useBatchDownload = (urls: string[]) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const downloadFile = useCallback((url: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = url.split('/').pop() || 'download';
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);

        setTimeout(resolve, 100);
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const startDownload = useCallback(async () => {
    setIsDownloading(true);
    setError(null);
    setCurrentIndex(-1);

    for (let i = 0; i < urls.length; i++) {
      try {
        setCurrentIndex(i);
        await downloadFile(urls[i]);
        // Wait 1 second between downloads
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred during download'
        );
        break;
      }
    }

    setIsDownloading(false);
  }, [urls, downloadFile]);

  return {
    isDownloading,
    currentIndex,
    error,
    startDownload,
  };
};

export default function DownloadGmailExportsDialog({
  open,
  handleClose,
  gmailAuditModel,
}: DownloadGmailExportsDialogProps) {
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [showDownloadDirectly, setShowDownloadDirectly] = useState(false);
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { features, changeLogRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { isDownloading, currentIndex, error, startDownload } =
    useBatchDownload(gmailAuditModel.downloadUrls);

  function handleDownloadAllFiles() {
    startDownload();
    createEvent(analytics.import_export.gmail_export.download_gmail_export, {
      module: ModuleList.modules.importExport.name,
      section: ModuleList.modules.importExport.sections.gmailExport,
    });
  }

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DownloadGmailExport.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      downloadGmailExportToDrive(
        gmailAuditModel,
        selectedFolderName,
        selectedFolder,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Gmail export to drive is now running.',
      });

      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue downloading the gmail export. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.gmail_export.download_gmail_export, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.gmailExport,
      });
    },
  });

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={'Files For Download'}
        dialogContent={
          <Box>
            <Typography>
              Select a folder to export to Google Drive. You will receive an
              email on completion.
            </Typography>

            <Box sx={{ my: 1 }}>
              {isFeatureEnabled('drivePickerUIReact', features) ? (
                <SharedFilePicker
                  pickerType="folder"
                  selectedFileId={selectedFolder || ''}
                  selectedFileName={selectedFolderName}
                  onFileSelected={({ id, name }) => {
                    setSelectedFolder(id);
                    setSelectedFolderName(name);
                  }}
                  onFileRemoved={() => {
                    setSelectedFolder('');
                    setSelectedFolderName('');
                  }}
                  includeSharedDrives
                />
              ) : (
                <SharedFilePickerOld
                  pickerType="folder"
                  selectedFileId={selectedFolder || ''}
                  selectedFileName={selectedFolderName}
                  onFileSelected={({ id, name }) => {
                    setSelectedFolder(id);
                    setSelectedFolderName(name);
                  }}
                  onFileRemoved={() => {
                    setSelectedFolder('');
                    setSelectedFolderName('');
                  }}
                  includeSharedDrives
                />
              )}
            </Box>

            <Stack alignItems="flex-end">
              <Button
                onClick={() => setShowDownloadDirectly(!showDownloadDirectly)}
                sx={{ width: 'fit-content', textTransform: 'none' }}
                size="small"
              >
                {showDownloadDirectly ? 'Hide' : 'Download Files Directly'}
                <ChevronRight
                  sx={{
                    transform: showDownloadDirectly
                      ? 'rotate(90deg)'
                      : 'rotate(0deg)',
                  }}
                />
              </Button>
            </Stack>

            <Collapse in={showDownloadDirectly}>
              <Divider sx={{ my: 1 }} />

              <Typography>You can also download the files directly.</Typography>

              <Stack gap={2} sx={{ my: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Download />}
                  onClick={handleDownloadAllFiles}
                  disabled={isDownloading}
                  sx={{ width: 'fit-content' }}
                >
                  {isDownloading
                    ? `Downloading ${currentIndex + 1} of ${
                        gmailAuditModel.downloadUrls.length
                      }`
                    : 'Download All'}
                </Button>
                {error && <Alert severity="error">{error}</Alert>}
                <Alert severity="info">
                  This may not work well if you have a lot of files to download.
                </Alert>
              </Stack>
            </Collapse>
          </Box>
        }
        primaryButton={{
          title: 'Export to Drive',
          action: handleSubmit,
          loading: isPending,
          disabled: !selectedFolder || !selectedFolderName,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate();
        }}
        mutateLoading={isPending}
      />
    </>
  );
}
